import { isNotDefined } from '@sgme/fp';
import { format } from 'date-fns';
import { v4 as uuid } from 'uuid';

import { monitorTimeout } from '@/api/monitorTimeout.ts';
import type { DealRejected, DealReply, FillReport } from '@/api/signalR/messages/execution.ts';
import { getSignalRConnector } from '@/api/signalR/SignalRConnector.ts';
import type { WebapiEndpointBuilder } from '@/api/webapi.ts';
import { cashApi } from '@/store/cash/api/index.ts';
import { selectCashTile } from '@/store/cash/selectors/tile.ts';
import {
  cashExecutionPending,
  cashExecutionRejected,
  cashExecutionReset,
  cashExecutionSuccess,
  cashRejectedExecutionReset,
} from '@/store/cash/slice.ts';
import { espApi } from '@/store/esp/api';
import type { EspStreamPriced } from '@/store/esp/state.ts';
import { getLadderIndexForNotional } from '@/store/esp/utils.ts';
import { buildHandleLateFillReport } from '@/store/growl/lateFillReport.ts';
import { getExecution, GrowlSlice, type GrowUpdate } from '@/store/growl/slice.ts';
import { updateGrowlApiErrorThunk } from '@/store/growl/updateGrowlApiErrorThunk.ts';
import { whenGrowlClosed } from '@/store/growl/whenGrowlClosed.ts';
import type { AppDispatch, AppState, GetAppState } from '@/store/store.ts';
import type { ISODate } from '@/types/date.ts';
import type { CurrencyIndex } from '@/utils/currencies/types.ts';
import type { Timestamp } from '@/utils/date.ts';
import { SECOND_IN_MILLIS } from '@/utils/time.ts';
import { toBase64 } from '@/utils/toBase64.ts';

export type ExecuteCashEspBody = {
  tileId: string;
  espPrice: EspStreamPriced;

  currency1: string; // "EUR"
  currency2: string; // "USD"
  notionalCurrencyIndex: CurrencyIndex; // "EUR"
  notional: number; // 1000000
  executionId: string; // "SGM_5fnjqaix3cwu7xwnisioz16wz"
  signalRConnectionId: string; // "KuvKOWVvnmrUYoo4ceYcdgiw3HeQd02"
  quoteId: string; // "bebdc911-3b67-445f-b652-20d41d567926"
  multipassTargetInstance: string; // "1"
  maturityDate: ISODate;
  clientSpotRate: number; // 1.1589
  clientId: number; // 20252
  way: 'Bid' | 'Ask'; // "Bid"
  userLocalDateTime: Timestamp;
  lastQuoteFromBackTimestamp: Timestamp; // 1737562001715
  lastQuoteReceivedByFrontTimestamp: Timestamp; // 1737562001653
  executionClickByFrontTimestamp: Timestamp; // 1737562001812
  notificationEmail: string;
} & (
  | { isNdf: false }
  | {
      isNdf: true;
      fixingSource: string; // "WMR1PMLN"
      fixingDate: ISODate; // "2025-01-24"
    }
);

export type ExecuteCashEspResponse = {};

export const executeCashEsp = (builder: WebapiEndpointBuilder) => {
  return builder.mutation<ExecuteCashEspResponse, ExecuteCashEspBody>({
    query: (body) => {
      const streamDepth = getLadderIndexForNotional(body.espPrice, body.notional, body.notionalCurrencyIndex) + 1;

      const delay = Date.now() - body.lastQuoteReceivedByFrontTimestamp;
      console.log('ESP execution delay', { delay });

      return {
        url: '/fxSpotFwd/esp/execute',
        method: 'POST',
        // headers: { 'X-Correlation-ID': body.executionId },
        body: {
          currency1: body.currency1,
          currency2: body.currency2,
          negotiatedCurrency: body.notionalCurrencyIndex === 1 ? body.currency1 : body.currency2,
          negotiatedAmount: body.notional,
          executionId: body.executionId,
          dataStreamId: body.signalRConnectionId,
          quoteId: body.quoteId,
          multipassTargetInstance: body.multipassTargetInstance,
          valueDate: `${body.maturityDate}T00:00:00.000Z`, // "2025-01-24T00:00:00.000Z"
          clientSpotRate: body.clientSpotRate,
          counterPartyBdrId: body.clientId,
          streamDepth,
          way: body.way,
          userLocalDateTime: format(body.userLocalDateTime, "yyyy-MM-dd'T'hh:mm:ss"),
          lastQuoteFromBackTimestamp: body.lastQuoteFromBackTimestamp,
          lastQuoteReceivedByFrontTimestamp: body.lastQuoteReceivedByFrontTimestamp,
          executionClickByFrontTimestamp: body.executionClickByFrontTimestamp,
          isNdf: body.isNdf,
          email64NotificationList: toBase64(body.notificationEmail),
        },
      };
    },

    async onQueryStarted(request, { queryFulfilled, getState, dispatch }) {
      const { tileId, executionId } = request;

      const stopMonitor = monitorTimeout(executionId, dispatch, {
        onExecFail: () => {
          dispatchCashExecutionRejected(tileId, executionId, dispatch);
        },
        onExecSuccess: () => {
          dispatchCashExecutionSuccess(tileId, executionId, getState as GetAppState, dispatch);
        },
        getPendingExecutionDetails: () => {
          const execution = getExecution(getState() as AppState, executionId);
          if (
            isNotDefined(execution) ||
            execution.type !== 'cashDealExecution' ||
            execution.data.status !== 'pending'
          ) {
            return undefined;
          }

          return {
            clientId: execution.data.data.accountId,
            currencyPair: `${execution.data.data.currency1}/${execution.data.data.currency2}`,
            productType: execution.data.productName,
            tradeDate: execution.data.data.tradeDate,
          };
        },
      });

      try {
        await queryFulfilled;
      } catch (error) {
        stopMonitor();
        dispatch(updateGrowlApiErrorThunk({ growlId: executionId, error }));
        dispatchCashExecutionRejected(tileId, executionId, dispatch);
      }
    },

    // see:
    // - listen signalR message: fx-web/app/src/Workers/SignalR/signalRStream.ts
    // - handle signalR message: fx-web/app/src/execution/handleExecutionStream.ts
    async onCacheEntryAdded(params, { cacheEntryRemoved, dispatch, getState }) {
      const handleReceiveDealReply = buildHandleReceiveDealReply(params, getState as GetAppState, dispatch);
      const handleReceiveDealRejected = buildHandleReceiveDealRejected(params, dispatch);
      const handleReceiveFillReport = buildHandleReceiveFillReport(params, getState as GetAppState, dispatch);

      const signalRConnector = getSignalRConnector();

      signalRConnector.on('ReceiveDealReply', handleReceiveDealReply);
      signalRConnector.on('ReceiveDealRejected', handleReceiveDealRejected);
      signalRConnector.on('ReceiveFillReport', handleReceiveFillReport);

      dispatch(
        GrowlSlice.actions.create({
          growlId: params.executionId,
          growl: {
            type: 'cashDealExecution',
            createdAt: Date.now(),
            options: { closeable: false },
            data: params.isNdf
              ? {
                  tileId: params.tileId,
                  productName: 'FxNdf',
                  status: 'pending',
                  data: {
                    way: params.way,
                    currency1: params.currency1,
                    currency2: params.currency2,

                    accountId: params.clientId,

                    notional: params.notional,
                    notionalCurrencyIndex: params.notionalCurrencyIndex,

                    dealtRate: params.clientSpotRate,
                    forwardDate: params.maturityDate,
                    reference: params.fixingSource,
                    fixingDate: params.fixingDate,
                    tradeDate: params.userLocalDateTime,
                  },
                }
              : {
                  tileId: params.tileId,
                  productName: 'FxSpot',
                  status: 'pending',
                  data: {
                    currency1: params.currency1,
                    currency2: params.currency2,
                    way: params.way,
                    notional: params.notional,
                    notionalCurrencyIndex: params.notionalCurrencyIndex,
                    dealtRate: params.clientSpotRate,
                    spotDate: params.maturityDate,
                    accountId: params.clientId,
                    tradeDate: params.userLocalDateTime,
                  },
                },
          },
        }),
      );

      dispatch(cashExecutionPending({ tileId: params.tileId, executionId: params.executionId }));

      // `cacheEntryRemoved` is always resolved explicitly via `removeMutationResult`,
      // called on every execution path: DealReply, DealRejected, FillReport, and timeout.
      // It is never left to RTK Query's automatic 1-hour cache expiry.
      await cacheEntryRemoved;

      signalRConnector.off('ReceiveDealReply', handleReceiveDealReply);
      signalRConnector.off('ReceiveDealRejected', handleReceiveDealRejected);
      signalRConnector.off('ReceiveFillReport', handleReceiveFillReport);

      // the back office can send the trade id long after the execution request is over
      // (NOT_YET_INTEGRATED): keep listening, but only on behalf of the growl, and only as long as
      // the user keeps it open
      const handleLateFillReport = buildHandleLateFillReport(params.executionId, dispatch);
      signalRConnector.on('ReceiveFillReport', handleLateFillReport);

      await whenGrowlClosed(params.executionId, getState as GetAppState);

      signalRConnector.off('ReceiveFillReport', handleLateFillReport);
    },
  });
};

// ███████╗██╗ ██████╗ ███╗   ██╗ █████╗ ██╗         ██████╗
// ██╔════╝██║██╔════╝ ████╗  ██║██╔══██╗██║         ██╔══██╗
// ███████╗██║██║  ███╗██╔██╗ ██║███████║██║         ██████╔╝
// ╚════██║██║██║   ██║██║╚██╗██║██╔══██║██║         ██╔══██╗
// ███████║██║╚██████╔╝██║ ╚████║██║  ██║███████╗    ██║  ██║
// ╚══════╝╚═╝ ╚═════╝ ╚═╝  ╚═══╝╚═╝  ╚═╝╚══════╝    ╚═╝  ╚═╝

// SEE: fx-web/app/src/Workers/SignalR/signalRStream.ts
const TIMEOUT_TOTAL_MILLIS = 10 * SECOND_IN_MILLIS;

const buildHandleReceiveDealReply = (params: ExecuteCashEspBody, getState: GetAppState, dispatch: AppDispatch) => {
  return (dealReply: DealReply) => {
    if (dealReply.Execute.ExecutionId !== params.executionId) {
      return;
    }

    dispatchCashExecutionSuccess(params.tileId, params.executionId, getState, dispatch);

    // FillReport is expected after DealReply and will provide trade IDs + close listeners.
    // If no FillReport arrives within TIMEOUT_TOTAL_MILLIS, close listeners as fallback.
    setTimeout(() => {
      const execution = getExecution(getState(), params.executionId);

      if (isNotDefined(execution) || execution.type !== 'cashDealExecution' || execution.data.status !== 'integrated') {
        return;
      }

      dispatch(cashApi.internalActions.removeMutationResult({ fixedCacheKey: params.executionId }));
    }, TIMEOUT_TOTAL_MILLIS);
  };
};

const dispatchCashExecutionSuccess = (tileId: string, rfsId: string, getState: GetAppState, dispatch: AppDispatch) => {
  dispatch(
    GrowlSlice.actions.update({
      growlId: rfsId,
      data: { status: 'integrated' },
      options: {
        closeable: true,
      },
    }),
  );

  dispatch(cashExecutionSuccess({ tileId }));

  setTimeout(() => {
    // FxFwd tiles lose maturityDateTenor after reset — capture it before dispatching
    const tileBeforeReset = selectCashTile(getState(), tileId);
    const maturityDateTenor = tileBeforeReset?.properties.maturityDateTenor;

    dispatch(cashExecutionReset({ tileId, userPreferences: getState().userPreferences, sessionId: uuid() }));

    // TODO: can be not a Cash tile if the user doesn't have access to Cash
    const resetedTile = selectCashTile(getState(), tileId);

    if (isNotDefined(resetedTile) || isNotDefined(resetedTile.tradeCapture)) {
      return;
    }

    dispatch(
      // @ts-expect-error special case when init FxCash
      espApi.endpoints.checkCashByTradeCapture.initiate({
        tileId,
        type: 'FxSpot',
        properties: {
          currencyPair: resetedTile.properties.currencyPair,
          notional: resetedTile.properties.notional,
          notionalCurrencyIndex: resetedTile.properties.notionalCurrencyIndex,
          maturityDate: maturityDateTenor !== undefined ? undefined : resetedTile.properties.maturityDate,
          maturityDateTenor: maturityDateTenor ?? resetedTile.properties.maturityDateTenor,
        },
      }),
    );
  }, SECOND_IN_MILLIS);
};

const buildHandleReceiveDealRejected = (params: ExecuteCashEspBody, dispatch: AppDispatch) => {
  return (rejectedDeal: DealRejected) => {
    if (rejectedDeal.Execute.ExecutionId !== params.executionId) {
      return;
    }

    const data: GrowUpdate = {
      status: 'rejected',
      errorCode: rejectedDeal.DealReject.ErrorCode,
      multipassErrorCode: rejectedDeal.DealReject.MultipassErrorCode,
      reason: rejectedDeal.DealReject.ErrorMessage,
      sessionId: rejectedDeal.DealReject.MultipassSessionId,
    };
    dispatch(GrowlSlice.actions.update({ growlId: params.executionId, data, options: { closeable: true } }));
    dispatchCashExecutionRejected(params.tileId, params.executionId, dispatch);
  };
};

const dispatchCashExecutionRejected = (tileId: string, executionId: string, dispatch: AppDispatch) => {
  dispatch(cashApi.internalActions.removeMutationResult({ fixedCacheKey: executionId }));
  dispatch(cashExecutionRejected({ tileId }));

  setTimeout(() => {
    dispatch(cashRejectedExecutionReset({ tileId }));
  }, 2 * SECOND_IN_MILLIS);
};

const buildHandleReceiveFillReport = (params: ExecuteCashEspBody, _getState: GetAppState, dispatch: AppDispatch) => {
  const updateGrowl = buildHandleLateFillReport(params.executionId, dispatch);

  return (fillReport: FillReport) => {
    if (fillReport.Execute.ExecutionId !== params.executionId) {
      return;
    }

    updateGrowl(fillReport);

    dispatch(cashApi.internalActions.removeMutationResult({ fixedCacheKey: params.executionId }));
  };
};
